import * as React from 'react';
import { ContentPage, IPageContent } from '../../types/Content';
import Avatar from '@mui/material/Avatar';
import { getMenuItemIcon, pageContent } from '../../helpers/menu';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { Link } from 'react-router-dom';
import { routes } from '../../Routes';

interface IFavoriteIcons {
  frontpageList?: IPageContent[];
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: '16px 0',
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    width: theme.spacing(10),
    height: theme.spacing(10),
    marginBottom: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      fontSize: '2rem',
      opacity: '0.9',
    },
    '& .fas': {
      fontSize: '1.7rem !important',
      opacity: '0.9',
    },
  },
  boxShadow: {
    boxShadow: `0 6px 20px rgba(var(--shadow-color), 0.50)`,
    '&:hover': {
      boxShadow: `0 6px 20px rgba(var(--shadow-color), 0.20)`,
    },
  },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  text: {
    fontSize: '0.9rem !important',
  },
}));

const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : null;
};

const FavoriteIcons: React.FC<IFavoriteIcons> = ({
  frontpageList
}): JSX.Element => {
  const classes = useStyles({});

  const truncateString = (str: string, num: number) => {
    if (str.length > num) {
      return str.slice(0, num) + '...';
    }
    return str;
  };

  const renderLink = (page: IPageContent) => {
    const isExternalLink = page.type === ContentPage.MENU_LINK;
    const commonProps = {
      className: classes.link,
      style: {
        backgroundColor: page.color,
        '--shadow-color': hexToRgb(page.color)
      } as React.CSSProperties,
    };

    const linkContent = (
      <Paper className={classes.itemContainer} elevation={0}>
        <Avatar className={`${classes.avatar} ${classes.boxShadow}`} style={commonProps.style}>
          {getMenuItemIcon(page)}
        </Avatar>
        <Typography className={classes.text} variant="body1" align="center">
          {truncateString(page.name, 17)}
        </Typography>
      </Paper>
    );

    if (isExternalLink && page.linkUrl) {
      return (
        <a
          href={page.linkUrl}
          className={commonProps.className}
          target={page.linkNewWindow ? '_blank' : ''}
          rel="noopener noreferrer"
        >
          {linkContent}
        </a>
      );
    } else {
      return (
        <Link
          to={page.id ? pageContent(page) : routes.home}
          className={commonProps.className}
        >
          {linkContent}
        </Link>
      );
    }
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={4} justifyContent="center">
        {frontpageList?.map((page) => (
          <Grid item xs={6} sm={4} md={2} key={page.id}>
            {renderLink(page)}
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default FavoriteIcons;